import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Box, Text, VStack, useMultiStyleConfig, Link, AspectRatio} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {A11y} from 'swiper/modules'
import type SwiperType from 'swiper'
import {Picture, Video} from '_uiComponents'
import {CLDImageAsset, CLDVideoAsset} from '_types'
import {useAnalytics, usePageDesigner, useIsElementInViewport, useViewport} from '_customHooks'
import {updateCloudinaryPDImage, updateCloudinaryPDPoster, buildVideoSources, buildEditorialVideoAnalyticsTitle, PAGE_DESIGNER_MODULES, createRedirectUrl} from '_utils'

export interface EditorialSliderItemData {
    element_type?: 'Image' | 'Video'
    cta_url?: string
    cld_image?: CLDImageAsset
    cld_video?: CLDVideoAsset
    media_ratio?: string
    /** Mobile-specific overrides (Page Designer). When present, used instead of the default on mobile. */
    cld_image_mobile?: CLDImageAsset
    cld_video_mobile?: CLDVideoAsset
    media_ratio_mobile?: string
    title?: string
    content_color?: string
    content_t_align?: string
    content_h_align?: string
    content_v_align?: string
    description?: string
    focal_point_x?: string
}

export interface EditorialSliderItem {
    data?: EditorialSliderItemData
    id?: string
    typeId?: string
}

export interface EditorialSliderRegion {
    components?: EditorialSliderItem[]
    id?: string
}

export interface EditorialSliderProps {
    data: {
        section_title?: string
        class_name?: string
        custom_css?: string
    }
    regions: EditorialSliderRegion[]
    listingLayout?: string
    analyticsList?: string
    positionStart: number
    analyticsName?: string
    analyticsID?: string
}

// Slide width ratios as fractions of the Swiper container width — must match the CSS.
// These are used to compute the exact wrapper translate before the CSS transition fires,
// so the animation always moves toward the correct final position.
//
//   tablet  (768–1023 px) : active = 75 %, inactive = 32 %, left-aligned (no centering)
//   desktop (1024 px+)    : active = 50 %, inactive = 24 %, centered
//
// translate formula:
//   offset  = activeIndex × (container × inactiveRatio + spaceBetween)
//   desktop : translate = container/2 − offset − (container × activeRatio)/2
//   tablet  : translate = −offset
const getSlideWRatios = (isDesktop: boolean) =>
    isDesktop ? {activeRatio: 0.5, inactiveRatio: 0.24, centered: true} : {activeRatio: 0.75, inactiveRatio: 0.32, centered: false}

const calcWrapperTranslate = (swiper: SwiperType, isDesktop: boolean): number => {
    const {activeIndex, width: container} = swiper
    const spaceB = typeof swiper.params.spaceBetween === 'number' ? swiper.params.spaceBetween : 0
    const {activeRatio, inactiveRatio, centered} = getSlideWRatios(isDesktop)
    const offset = activeIndex * (container * inactiveRatio + spaceB)
    return centered ? container / 2 - offset - (container * activeRatio) / 2 : -offset
}

/**
 * applyWrapperTranslate Moves the Swiper wrapper to the calculated translate position.
 * - instant=true (resize, init): disables the CSS transition momentarily so the jump is imperceptible,
 *   forces a style flush via getBoundingClientRect, then re-enables the transition.
 * - instant=false (slide change): defers via requestAnimationFrame so Swiper's synchronous
 *   speed=0 cleanup (setTransition(0) → transitionDuration='0ms', fired both before and after
 *   onSlideChangeTransitionStart in the same JS frame) has already completed before we set the
 *   transition. Uses setProperty with !important for an additional layer of safety.
 */
const applyWrapperTranslate = (swiper: SwiperType, instant: boolean, isDesktop: boolean): void => {
    if (instant) {
        const translate = calcWrapperTranslate(swiper, isDesktop)
        swiper.wrapperEl.style.setProperty('transition', 'none', 'important')
        swiper.wrapperEl.style.transform = `translate3d(${translate}px, 0px, 0px)`
        swiper.wrapperEl.getBoundingClientRect() // force flush styles
        swiper.wrapperEl.style.removeProperty('transition')
    } else {
        const translate = calcWrapperTranslate(swiper, isDesktop) // compute now — activeIndex is correct
        requestAnimationFrame(() => {
            if (swiper.destroyed) return
            swiper.wrapperEl.style.setProperty('transition', 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)', 'important')
            swiper.wrapperEl.style.transform = `translate3d(${translate}px, 0px, 0px)`
        })
    }
}
/**
 * addMobilePortraitCrop Inserts a chained Cloudinary transformation in the URL for mobile crop.
 *
 * Uses `c_fill` with `g_auto` and an aspect-ratio qualifier. Without explicit width/height
 * Cloudinary scales relative to the original dimensions and crops to the requested ratio.
 * `g_auto` ensures the most visually important area is preserved.
 *
 * @see https://cloudinary.com/documentation/image_crop_modes — `fill` section
 * @see https://cloudinary.com/documentation/image_automatic_gravity — `g_auto` details
 *
 * @param url - The Cloudinary image/video URL.
 * @param ratio - Optional aspect ratio from the backend (percentage-based, e.g. '56.25' for 16:9,
 *                meaning height/width × 100). When absent, the default mobile crop 327:400 is used.
 */
const addMobilePortraitCrop = (url: string, ratio?: string): string => {
    if (!url) return url
    const ar = ratio ? `100:${ratio}` : '327:400'
    const cropSegment = `c_fill,g_auto,ar_${ar}`
    if (/\/v\d+\//.test(url)) {
        return url.replace(/(\/v\d+\/)/, `/${cropSegment}$1`)
    }
    return url.replace(/\/([^/]+)$/, `/${cropSegment}/$1`)
}

const EditorialSlider: React.FC<EditorialSliderProps> = ({data, regions, ...rest}) => {
    const {section_title, class_name, custom_css} = data ?? {}
    const {formatMessage} = useIntl()
    const viewAllLabel = formatMessage({id: 'editorial_slider.link.view_all', defaultMessage: 'View all'})
    const styles = useMultiStyleConfig('EditorialSlider', {}) as Record<string, any>
    const swiperRef = useRef<SwiperType | null>(null)
    const thumbsSwiperRef = useRef<SwiperType | null>(null)
    const targetElement = useRef<HTMLDivElement>(null)
    const {isMobile, isDesktop} = useViewport()
    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const isInViewport = useIsElementInViewport(targetElement)
    const {globalImageTransformation, globalVideoTransformation, CloudinaryPageDesignerCNAME, linkObject} = usePageDesigner()

    // Locale-aware link to the Stories page. createRedirectUrl only rewrites absolute
    // Page Designer URLs (/s/{site}/{locale}/…); for a fixed internal path we build the
    // href from linkObject directly. Falls back to a locale-prefixed relative path when
    // the host is not available (e.g. SSR before hostLink parsing). The host is normalized
    // so an already-schemed value never produces a double "https://".
    const viewAllHref = linkObject.hostlink
        ? `https://${linkObject.hostlink.replace(/^https?:\/\//, '')}/${linkObject.locale}/stories.html`
        : `/${linkObject.locale}/stories.html`

    /**
     * Sync the active-title highlight in the nav Swiper.
     * @param mainActiveIndex — the activeIndex of the main slider (0-based, items only).
     * The nav has "View All" at index 0, so the real title for main slide N is at nav index N+1.
     */
    const syncActiveTitle = (mainActiveIndex: number) => {
        const nav = thumbsSwiperRef.current
        if (!nav || nav.destroyed || !nav.slides) return
        nav.slides.forEach((slide, i) => {
            slide.classList.toggle('swiper-slide-thumb-active', i === mainActiveIndex + 1)
        })
        // Scroll the nav so the active title is visible (useful on mobile).
        nav.slideTo(mainActiveIndex + 1)
    }

    const [alreadySentPromoView, setAlreadySentPromoView] = useState(false)

    // Re-apply instant centering whenever isDesktop changes (e.g. after viewport hydration).
    // onSwiper fires once at init and captures isDesktop via closure — if isDesktop was still
    // false at that point, this effect corrects the wrapper position once the value is known.
    useEffect(() => {
        const swiper = swiperRef.current
        if (!swiper || swiper.destroyed || swiper.params.slidesPerView !== 'auto') return
        if (!getSlideWRatios(isDesktop).centered) return
        requestAnimationFrame(() => {
            if (!swiperRef.current || swiperRef.current.destroyed) return
            applyWrapperTranslate(swiperRef.current, true, isDesktop)
        })
    }, [isDesktop])

    // Custom wheel handler for horizontal trackpad scroll on desktop/tablet.
    // The built-in Swiper Mousewheel module doesn't work with virtualTranslate,
    // so we intercept wheel events directly and navigate one slide per gesture.
    useEffect(() => {
        const swiper = swiperRef.current
        if (!swiper || swiper.destroyed) return

        const el = swiper.el
        let wheelCooldown = false

        const handleWheel = (e: WheelEvent) => {
            // Only handle horizontal scroll (trackpad two-finger horizontal gesture).
            // Ignore if mostly vertical (user scrolling the page).
            if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return
            // Ignore small accidental horizontal movements.
            if (Math.abs(e.deltaX) < 5) return
            // Only act on md+ (where virtualTranslate is active).
            if (swiper.params.slidesPerView !== 'auto') return

            // Always prevent default for horizontal gestures over the slider to stop
            // the browser from interpreting them as "navigate back/forward" in history.
            e.preventDefault()
            e.stopPropagation()

            if (wheelCooldown) return
            // Require a stronger gesture to actually change slide (avoids accidental navigation).
            if (Math.abs(e.deltaX) < 30) return
            wheelCooldown = true

            if (e.deltaX > 0) {
                swiper.slideNext()
            } else {
                swiper.slidePrev()
            }

            // Cooldown: ignore further wheel events for 600ms to prevent
            // trackpad inertia from triggering multiple slide changes.
            setTimeout(() => {
                wheelCooldown = false
            }, 600)
        }

        el.addEventListener('wheel', handleWheel, {passive: false})
        return () => {
            el.removeEventListener('wheel', handleWheel)
        }
    }, [isMobile])

    useEffect(() => {
        if (!alreadySentPromoView && isInViewport) {
            analyticsEventPromoView({
                id: rest.analyticsID ? rest.analyticsID : 'null',
                position: rest.positionStart,
                creative: PAGE_DESIGNER_MODULES.EDITORIAL_SLIDER,
                name: rest.analyticsName ? rest.analyticsName : 'null',
            })
            setAlreadySentPromoView(true)
        }
    }, [isInViewport, alreadySentPromoView])

    const items = regions?.[0]?.components ?? []

    return (
        <VStack
            {...styles.wrapper}
            className={class_name}
            data-testid="editorial-slider"
            sx={custom_css ? {[`& .editorial-slider-root`]: {}} : undefined}
            ref={targetElement}
        >
            {section_title && (
                <Text {...styles.title} className="cc-title">
                    {section_title}
                </Text>
            )}

            {items.length > 0 && (
                <Box sx={styles.titleNav}>
                    <Swiper
                        className="editorial-slider-title-nav"
                        modules={[]}
                        slidesPerView="auto"
                        spaceBetween={20}
                        breakpoints={{
                            0: {centeredSlides: true, allowTouchMove: true, spaceBetween: 20},
                            768: {centeredSlides: false, allowTouchMove: false, spaceBetween: 30},
                            1024: {centeredSlides: false, allowTouchMove: false, spaceBetween: 40},
                        }}
                        onSwiper={(swiper) => {
                            thumbsSwiperRef.current = swiper
                            // Set initial active state on first real title (index 1 = first voice).
                            requestAnimationFrame(() => {
                                if (swiper.destroyed || !swiper.slides) return
                                swiper.slides.forEach((slide, i) => {
                                    slide.classList.toggle('swiper-slide-thumb-active', i === 1)
                                })
                            })
                        }}
                    >
                        {/* "View All" as the first nav item — scrollable with titles on mobile, static on md+.
                            It's a link (not a thumb), so clicking navigates away rather than changing slides. */}
                        <SwiperSlide className="editorial-slider-title-nav-slide editorial-slider-view-all-slide">
                            <Box
                                as={Link}
                                href={viewAllHref}
                                data-testid="editorial-slider-view-all"
                                textDecoration="none"
                                _hover={{textDecoration: 'none', color: 'color.text.black'}}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                                {viewAllLabel}
                            </Box>
                        </SwiperSlide>
                        {items.map((item, idx) => (
                            <SwiperSlide
                                key={`title-${item.id ?? idx}`}
                                className="editorial-slider-title-nav-slide"
                                onClick={() => {
                                    // Navigate main slider and immediately sync the active title highlight.
                                    if (swiperRef.current && !swiperRef.current.destroyed) {
                                        swiperRef.current.slideTo(idx)
                                    }
                                    syncActiveTitle(idx)
                                }}
                            >
                                {item.data?.title ?? ''}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            )}

            <Box sx={styles.swiperWrapper}>
                <Swiper
                    modules={[A11y]}
                    className="editorial-slider-swiper"
                    autoHeight
                    simulateTouch
                    breakpoints={{
                        0: {slidesPerView: 1.1, centeredSlides: true, spaceBetween: 8, speed: 300},
                        768: {slidesPerView: 'auto', centeredSlides: true, spaceBetween: 8, speed: 0, virtualTranslate: true, allowTouchMove: true},
                        1024: {slidesPerView: 'auto', centeredSlides: true, spaceBetween: 8, speed: 0, virtualTranslate: true, allowTouchMove: true},
                    }}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper
                        if (swiper.params.slidesPerView === 'auto') {
                            requestAnimationFrame(() => {
                                // Desktop: center the first slide instantly (virtualTranslate keeps Swiper from overriding the transform).
                                // Tablet: translateX(0) is already correct for left-aligned layout, nothing to do.
                                if (getSlideWRatios(isDesktop).centered) applyWrapperTranslate(swiper, true, isDesktop)
                            })
                        }
                    }}
                    onSlideChange={(swiper) => {
                        syncActiveTitle(swiper.activeIndex)
                    }}
                    onSlideChangeTransitionStart={(swiper) => {
                        if (swiper.params.slidesPerView !== 'auto') return
                        applyWrapperTranslate(swiper, false, isDesktop) // animated by CSS transition
                    }}
                >
                    {items.map((item, idx) => {
                        const {
                            cld_image,
                            cld_video,
                            cld_image_mobile,
                            cld_video_mobile,
                            media_ratio_mobile,
                            element_type,
                            title,
                            focal_point_x,
                            description,
                            cta_url,
                            media_ratio,
                        } = item.data ?? {}
                        const isVideo = element_type === 'Video' && !!cld_video

                        // --- Resolve media assets per breakpoint ---
                        // Desktop/Tablet: always use the default assets.
                        // Mobile: prefer the mobile-specific asset when provided by PD; fall back to the default otherwise.
                        const effectiveImage = isMobile && cld_image_mobile ? cld_image_mobile : cld_image
                        const effectiveVideo = isMobile && cld_video_mobile ? cld_video_mobile : cld_video
                        const effectiveRatio = isMobile && media_ratio_mobile ? media_ratio_mobile : media_ratio

                        // Parametric responsive video sources (replaces the single-URL build + mobile crop).
                        // The builder already emits per-breakpoint widths/formats; the dedicated mobile asset
                        // (when present) is picked up via effectiveVideo. No g_auto crop on video (that path
                        // was buggy — see specs §2.4 — and is unnecessary with responsive sources).
                        const videoSources = isVideo ? buildVideoSources(effectiveVideo, {slot: 'editorialSlider', transformation: globalVideoTransformation, cname: CloudinaryPageDesignerCNAME}) : []
                        const posterUrl = isVideo && effectiveVideo ? updateCloudinaryPDPoster(effectiveVideo, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''
                        const videoAnalyticsTitle = buildEditorialVideoAnalyticsTitle(formatMessage, idx + 1, 1)

                        // Player options from the Cloudinary video form (not hardcoded): read the editor's
                        // autoplay/muted/loop/controls. Defaults preserve the historical behaviour when a flag
                        // is absent. The atom applies the rule controls=true ⇒ autoplay=false centrally.
                        const videoPlayerConf = isVideo && effectiveVideo?.playerConf ? JSON.parse(effectiveVideo.playerConf)?.playerConfig : undefined
                        const videoAutoplay = typeof videoPlayerConf?.autoplay === 'boolean' ? videoPlayerConf.autoplay : true
                        const videoMuted = typeof videoPlayerConf?.muted === 'boolean' ? videoPlayerConf.muted : true
                        const videoLoop = typeof videoPlayerConf?.loop === 'boolean' ? videoPlayerConf.loop : true
                        const videoControls = typeof videoPlayerConf?.controls === 'boolean' ? videoPlayerConf.controls : false

                        // addMobilePortraitCrop is a FALLBACK for IMAGES only: applied when no dedicated mobile
                        // asset is provided. If cld_image_mobile exists, the data entry uploads cropped media.
                        const hasMobileImage = isMobile && !!cld_image_mobile

                        const imgUrl =
                            !isVideo && effectiveImage ? updateCloudinaryPDImage(effectiveImage, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''

                        const mobileImgUrl = hasMobileImage ? imgUrl : addMobilePortraitCrop(imgUrl, media_ratio_mobile)

                        // On mobile, if a dedicated mobile ratio is provided we use AspectRatio; otherwise
                        // the CSS aspect-ratio (327:400) on the crop box handles the framing.
                        const useMobileRatio = isMobile && !!effectiveRatio && !!media_ratio_mobile

                        const slideContent = (
                            <>
                                <Box className="editorial-slider-crop">
                                    {isVideo ? (
                                        <Box
                                            className="editorial-slider-picture"
                                            position="relative"
                                            left="50%"
                                            width={{base: '100%', md: '75vw', lg: '50vw'}}
                                            height="auto"
                                        >
                                            {(!isMobile && media_ratio) || useMobileRatio ? (
                                                <AspectRatio ratio={Number(effectiveRatio)}>
                                                    <Video
                                                        sources={videoSources}
                                                        poster={posterUrl}
                                                        analyticsTitle={videoAnalyticsTitle}
                                                        objectFit="cover"
                                                        autoplay={videoAutoplay}
                                                        muted={videoMuted}
                                                        loop={videoLoop}
                                                        controls={videoControls}
                                                        width="100%"
                                                        height="100%"
                                                    />
                                                </AspectRatio>
                                            ) : (
                                                <Video
                                                    sources={videoSources}
                                                    poster={posterUrl}
                                                    analyticsTitle={videoAnalyticsTitle}
                                                    objectFit="cover"
                                                    autoplay={videoAutoplay}
                                                    muted={videoMuted}
                                                    loop={videoLoop}
                                                    controls={videoControls}
                                                    width="100%"
                                                    height="100%"
                                                />
                                            )}
                                        </Box>
                                    ) : (
                                        <Box
                                            className="editorial-slider-picture"
                                            position="relative"
                                            left="50%"
                                            width={{base: '100%', md: '75vw', lg: '50vw'}}
                                            height="auto"
                                        >
                                            {(!isMobile && media_ratio) || useMobileRatio ? (
                                                <AspectRatio ratio={Number(effectiveRatio)}>
                                                    <Picture
                                                        imagexl={imgUrl}
                                                        imagelg={imgUrl}
                                                        imagemd={imgUrl}
                                                        imagesm={mobileImgUrl}
                                                        imagexs={mobileImgUrl}
                                                        objectFit="cover"
                                                        alt={title ?? ''}
                                                        width="100%"
                                                        height="100%"
                                                        hasLazy={false}
                                                    />
                                                </AspectRatio>
                                            ) : (
                                                <Picture
                                                    imagexl={imgUrl}
                                                    imagelg={imgUrl}
                                                    imagemd={imgUrl}
                                                    imagesm={mobileImgUrl}
                                                    imagexs={mobileImgUrl}
                                                    alt={title ?? ''}
                                                    objectFit="cover"
                                                    width="100%"
                                                    height="100%"
                                                    hasLazy={false}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                <Text className="editorial-slider-description">{description ?? ''}</Text>
                            </>
                        )

                        return (
                            <SwiperSlide
                                key={item.id ?? idx}
                                className="editorial-slider-slide"
                                style={{'--focal-x': (focal_point_x ?? 50) + '%'} as React.CSSProperties}
                            >
                                {cta_url ? (
                                    <Box
                                        as={Link}
                                        href={createRedirectUrl(cta_url, linkObject.hostlink, linkObject.locale, linkObject.site)}
                                        display="flex"
                                        flexDirection="column"
                                        w="100%"
                                        textDecoration="none"
                                        _hover={{textDecoration: 'none'}}
                                        onClick={() =>
                                            analyticsEventPromoClick({
                                                id: rest.analyticsID ? rest.analyticsID : 'null',
                                                position: String(rest.positionStart) + '-' + String(idx + 1),
                                                creative: PAGE_DESIGNER_MODULES.EDITORIAL_SLIDER,
                                                promoText: description ?? '',
                                                name: rest.analyticsName ? rest.analyticsName : 'null',
                                            })
                                        }
                                    >
                                        {slideContent}
                                    </Box>
                                ) : (
                                    slideContent
                                )}
                            </SwiperSlide>
                        )
                    })}
                </Swiper>
            </Box>
        </VStack>
    )
}

export {EditorialSlider}
